<div class="min-h-screen flex flex-col font-sans bg-white overflow-x-hidden">

  <!-- ═══════════════════════════════════════════════════════════════════════
       HERO — compact, monochrome, filter pills inside
       ─────────────────────────────────────────────────────────────────────
       PHOTO SWAP: add right after <section>:
         <img src="/assets/images/hero-venue.jpg" alt="Espacio en Madrid"
              class="absolute inset-0 w-full h-full object-cover object-center" />
       Then set the dark overlay div to bg-black/60.
  ═══════════════════════════════════════════════════════════════════════ -->
  <section class="relative overflow-hidden bg-[#0a0f18]">

    <!-- Hero photo -->
    <img
      src="assets/images/hero-venue.webp"
      alt="Espacio en Madrid"
      class="absolute inset-0 w-full h-full object-cover object-center"
      fetchpriority="high" />

    <!-- Dark overlay for text contrast -->
    <div
      class="pointer-events-none absolute inset-0 bg-black/55"
      aria-hidden="true"></div>

    <!-- Editorial vignette: darkens edges, soft center -->
    <div
      class="pointer-events-none absolute inset-0"
      style="background: radial-gradient(ellipse 70% 60% at 50% 45%, transparent 40%, rgba(0,0,0,0.35) 100%);"
      aria-hidden="true"></div>

    <!-- Film grain -->
    <div
      class="pointer-events-none absolute inset-0 opacity-[0.03]"
      style="background-image: url('data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2264%22 height=%2264%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22 numOctaves=%224%22/></filter><rect width=%2264%22 height=%2264%22 filter=%22url(%23n)%22/></svg>');"
      aria-hidden="true"></div>

    <!-- Bottom fade into white page -->
    <div
      class="pointer-events-none absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent"
      aria-hidden="true"></div>

    <!-- Content -->
    <div class="relative z-10 w-full max-w-4xl mx-auto box-border px-6 lg:px-10 py-16 sm:py-20 text-center">

      <!-- Eyebrow -->
      <p class="text-white/30 text-[11px] font-medium uppercase tracking-[0.28em] mb-6">
        Madrid &nbsp;·&nbsp; Espacios únicos &nbsp;·&nbsp; Por horas
      </p>

      <!-- H1 -->
      <h1
        class="text-[36px] sm:text-[52px] lg:text-[62px] font-bold leading-[1.05] tracking-tight text-white mb-4 sm:mb-5">
        El espacio para<br class="hidden sm:block" /> tu próxima idea.
      </h1>

      <!-- Subheadline -->
      <p class="text-[15px] sm:text-[17px] leading-[1.65] text-white/40 max-w-lg mx-auto mb-9 sm:mb-10">
        De celebraciones privadas a sesiones de fotografía o reuniones de equipo.
        Cada espacio en Madrid, disponible por horas.
      </p>

      <!-- ── Category filter pills ──────────────────────────────────────── -->
      <div class="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
        @for (cat of categories(); track cat.label) {
          <button
            type="button"
            (click)="exploreCategory(cat.filter)"
            class="inline-flex items-center gap-1.5 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full
                   text-[12px] sm:text-[13px] font-medium text-white/80
                   bg-white/8 hover:bg-white/18 border border-white/10 hover:border-white/22
                   transition-all duration-200 cursor-pointer
                   focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-0">
            {{ cat.label }}
          </button>
        }
        <a
          routerLink="/explorar"
          class="inline-flex items-center gap-1 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full
                 text-[12px] sm:text-[13px] font-medium text-white/35 hover:text-white/60
                 transition-colors duration-200">
          Ver todos
          <lucide-icon name="arrow-right" [size]="11" class="text-white/25" />
        </a>
      </div>

    </div>
  </section>

  <!-- ═══════════════════════════════════════════════════════════════════════
       ESPACIOS + CATEGORÍAS — contained shell (design.md max-w-7xl)
  ═══════════════════════════════════════════════════════════════════════ -->
  <div class="tfr-page-shell">

  <section class="bg-white py-10 sm:py-14 border-b border-gray-100">

      <!-- Section header -->
      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <h2 class="text-[22px] sm:text-[26px] font-bold leading-[1.2] text-gray-900">
            Disponibles ahora en Madrid
          </h2>
          <p class="text-[14px] text-gray-400 mt-1 font-medium">
            Precio visible. Reserva confirmada al instante.
          </p>
        </div>
        <a
          routerLink="/explorar"
          class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-400
                 hover:text-gray-900 transition-colors shrink-0">
          Explorar todos
          <lucide-icon name="arrow-right" [size]="15" />
        </a>
      </div>

      <!-- Spaces grid -->
      @if (spacesLoading()) {
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          @for (_ of [1,2,3]; track $index) {
            <div class="rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
              <div class="h-52 bg-gray-100"></div>
              <div class="p-5">
                <div class="h-4 bg-gray-100 rounded w-3/4 mb-2"></div>
                <div class="h-3 bg-gray-100 rounded w-1/2"></div>
              </div>
            </div>
          }
        </div>
      } @else if (latestSpaces().length === 0) {
        <div class="text-center py-16">
          <p class="text-gray-400 text-[15px] mb-4">Pronto habrá espacios disponibles en esta zona.</p>
          <a routerLink="/explorar" class="text-[13px] font-semibold text-gray-900 hover:text-action-blue transition-colors">
            Explorar catálogo completo →
          </a>
        </div>
      } @else {
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          @for (space of latestSpaces(); track space.id) {
            <a
              [routerLink]="spaceLink(space)"
              class="group flex flex-col bg-white rounded-2xl border border-gray-100 overflow-hidden
                     transition-all duration-300
                     hover:-translate-y-1 hover:border-gray-200 hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)]">

              <!-- Photo -->
              <div class="relative h-52 overflow-hidden bg-gray-50">
                @if (coverThumb(space)) {
                  <img
                    [ngSrc]="coverThumb(space)"
                    [alt]="space.name"
                    fill
                    [priority]="$index === 0"
                    class="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    [sizes]="cardImageSizes" />
                } @else {
                  <div class="w-full h-full flex items-center justify-center bg-gray-100">
                    <lucide-icon name="building-2" [size]="36" class="text-gray-300" />
                  </div>
                }
                <span
                  class="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[10px] font-semibold
                         bg-white/90 backdrop-blur-sm text-gray-700 shadow-sm ring-1 ring-black/5">
                  {{ spaceCategoryLabel(space) }}
                </span>
              </div>

              <!-- Info -->
              <div class="flex flex-col flex-1 p-5">
                <h3
                  class="text-[15px] font-semibold leading-snug text-gray-900 mb-1 truncate"
                  [attr.title]="space.name">
                  {{ space.name }}
                </h3>
                <p class="text-[12px] text-gray-400 flex items-center gap-1 mb-4 min-w-0">
                  <lucide-icon name="map-pin" [size]="11" class="shrink-0" />
                  <span class="truncate">{{ spaceLocationLabel(space) }}</span>
                </p>
                <div class="mt-auto flex items-center justify-between gap-2">
                  @if (hourlyRate(space) > 0) {
                    <span class="text-[15px] font-bold text-gray-900 tabular-nums">
                      Desde {{ hourlyRate(space) }}&euro;
                      <span class="text-[12px] font-normal text-gray-400">/ hora</span>
                    </span>
                  } @else {
                    <span class="text-[13px] text-gray-400">Consultar tarifa</span>
                  }
                  <span
                    class="text-[11px] font-semibold text-gray-900 tracking-wide shrink-0
                           opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Ver espacio →
                  </span>
                </div>
              </div>
            </a>
          }
        </div>
      }

  </section>

  <!-- ═══════════════════════════════════════════════════════════════════════
       CATEGORÍAS — 4 photo-window cards, monochrome neutral placeholder
       ─────────────────────────────────────────────────────────────────────
       PHOTO SWAP per card: add inside <button>:
         <img src="/assets/images/cat-celebraciones.jpg" alt="Celebraciones"
              class="absolute inset-0 w-full h-full object-cover object-center
                     transition-transform duration-500 group-hover:scale-[1.03]" />
  ═══════════════════════════════════════════════════════════════════════ -->
  <section class="bg-white py-16 lg:py-20 border-b border-gray-100">

      <p class="text-[10px] font-black uppercase tracking-[0.22em] text-gray-400 mb-3">
        Categorías
      </p>
      <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <h2 class="text-[22px] sm:text-[26px] font-bold text-gray-900 leading-tight">
            Encuentra el espacio que buscas
          </h2>
          <p class="text-[14px] text-gray-400 mt-1 font-medium">
            Selecciona el tipo de espacio que mejor se adapta a tu proyecto.
          </p>
        </div>
        <a
          routerLink="/explorar"
          class="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-400
                 hover:text-gray-900 transition-colors shrink-0">
          Ver todos
          <lucide-icon name="arrow-right" [size]="15" />
        </a>
      </div>

      <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        @for (cat of categories(); track cat.label) {
          <button
            type="button"
            (click)="exploreCategory(cat.filter)"
            class="group relative h-52 sm:h-64 rounded-xl sm:rounded-2xl overflow-hidden text-left cursor-pointer
                   focus:outline-none focus:ring-2 focus:ring-gray-900/20 focus:ring-offset-2">

            <!-- Neutral dark placeholder -->
            <div
              class="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.04]"
              [style.background]="cat.placeholderBg"
              aria-hidden="true"></div>

            <!-- Vignette -->
            <div
              class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"
              aria-hidden="true"></div>

            <!-- Hover arrow -->
            <div
              class="absolute top-4 right-4 h-7 w-7 rounded-full bg-white/10 backdrop-blur-sm
                     flex items-center justify-center ring-1 ring-white/15
                     opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0
                     transition-all duration-300">
              <lucide-icon name="arrow-right" [size]="12" class="text-white" />
            </div>

            <!-- Label -->
            <div class="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
              <p class="text-white font-bold text-[15px] sm:text-[17px] leading-tight tracking-tight">
                {{ cat.label }}
              </p>
              <p class="text-white/45 text-[10px] sm:text-[11px] mt-1 leading-snug font-medium">
                {{ cat.sublabel }}
              </p>
            </div>

          </button>
        }
      </div>
  </section>

  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════
       PUENTE PROPIETARIOS — una línea, antes del footer
  ═══════════════════════════════════════════════════════════════════════ -->
  <div class="border-t border-gray-100 bg-white">
    <div
      class="tfr-page-shell py-4
             flex flex-col sm:flex-row items-center justify-center gap-2">
      <p class="text-[12px] text-gray-400 text-center">
        ¿Tienes un local con horas disponibles?
      </p>
      <a
        routerLink="/propietarios"
        class="inline-flex items-center gap-1 text-[12px] font-semibold text-gray-900
               hover:text-action-blue transition-colors">
        Publícalo gratis en TimeFlexRent
        <lucide-icon name="arrow-right" [size]="13" />
      </a>
    </div>
  </div>

  <app-site-footer />
</div>
